import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import GroupIcon from '@mui/icons-material/Group';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BarChartIcon from '@mui/icons-material/BarChart';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChatIcon from '@mui/icons-material/Chat';
import MessageIcon from '@mui/icons-material/Message';
import BoltIcon from '@mui/icons-material/Bolt';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorageIcon from '@mui/icons-material/Storage';

export default function SuperAdmin() {
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [availableLLMProviders, setAvailableLLMProviders] = useState([]);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [usersModalOpen, setUsersModalOpen] = useState(false);
  const [statsModalOpen, setStatsModalOpen] = useState(false);

  // Selected company for modals
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [companyStats, setCompanyStats] = useState(null);

  // Form states
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const getAuthToken = () => localStorage.getItem('cme_token');

  // Load LLM providers
  const loadLLMProviders = useCallback(async () => {
    const token = getAuthToken();
    try {
      const res = await fetch('/companies/config/llm-providers', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAvailableLLMProviders(data.providers || []);
      }
    } catch (error) {
      console.error('Error loading LLM providers:', error);
      // Fallback providers
      setAvailableLLMProviders([
        { id: 'openai', name: 'OpenAI' },
        { id: 'gemini', name: 'Google Gemini' },
        { id: 'deepseek', name: 'DeepSeek' },
        { id: 'claude', name: 'Anthropic Claude' },
      ]);
    }
  }, []);

  // Load companies
  const loadCompanies = useCallback(async () => {
    const token = getAuthToken();
    try {
      const res = await fetch('/companies?limit=100', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load companies');
      const data = await res.json();
      setCompanies(data);

      // Calculate total users
      let total = 0;
      for (const company of data) {
        try {
          const usersRes = await fetch(`/companies/${company.id}/users`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (usersRes.ok) {
            const users = await usersRes.json();
            total += users.length;
          }
        } catch (e) {
          console.error(`Failed to load users for company ${company.id}:`, e);
        }
      }
      setTotalUsers(total);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  }, []);

  // Initialize
  useEffect(() => {
    const init = async () => {
      const token = getAuthToken();
      if (!token) {
        window.location.href = '/';
        return;
      }

      try {
        await loadLLMProviders();

        // Get current user
        const userRes = await fetch('/auth/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!userRes.ok) {
          // Token invalid - clear and redirect to login
          localStorage.removeItem('cme_token');
          localStorage.removeItem('cme_user');
          window.location.href = '/';
          return;
        }
        const user = await userRes.json();

        if (user.role !== 'super_admin') {
          alert('Access denied. This page is for super administrators only.');
          window.location.href = '/';
          return;
        }

        setCurrentUser(user);

        // Set document title for Super Admin
        document.title = 'Super Admin - CME';

        await loadCompanies();
        setLoading(false);
      } catch (error) {
        console.error('Initialization error:', error);
        alert('Failed to load page. Please try again.');
        window.location.href = '/';
      }
    };

    init();
  }, [loadCompanies, loadLLMProviders]);

  const handleLogout = () => {
    localStorage.removeItem('cme_token');
    localStorage.removeItem('cme_user');
    window.location.href = '/';
  };

  const handleAccessCompany = async (company) => {
    if (!confirm(`Access "${company.name}" as Company Admin?\n\nYou will be able to manage this company with full admin privileges.`)) {
      return;
    }

    const token = getAuthToken();
    try {
      const res = await fetch(`/auth/impersonate-company/${company.id}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to access company');
      }

      const data = await res.json();

      localStorage.setItem('cme_original_token', token);
      localStorage.setItem('cme_token', data.access_token);
      localStorage.setItem('cme_user', JSON.stringify(data.user));
      localStorage.setItem('cme_impersonating', 'true');
      // Note: GPT selection comes from impersonated user's profile (last_selected_gpt)

      window.location.href = '/';
    } catch (error) {
      console.error('Error accessing company:', error);
      alert('Failed to access company: ' + error.message);
    }
  };

  const handleDeleteCompany = async (company) => {
    if (!confirm(`Are you sure you want to delete "${company.name}"?\n\nThis will delete all users, GPTs, and data associated with this company. This action cannot be undone!`)) {
      return;
    }

    const token = getAuthToken();
    try {
      const res = await fetch(`/companies/${company.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to delete company');

      alert('Company deleted successfully!');
      await loadCompanies();
    } catch (error) {
      console.error('Error deleting company:', error);
      alert('Failed to delete company. Please try again.');
    }
  };

  const handleViewUsers = async (company) => {
    setSelectedCompany(company);
    setCompanyUsers([]);
    setUsersModalOpen(true);

    const token = getAuthToken();
    try {
      const res = await fetch(`/companies/${company.id}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load users');
      const users = await res.json();
      setCompanyUsers(users);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleViewStats = async (company) => {
    setSelectedCompany(company);
    setCompanyStats(null);
    setStatsModalOpen(true);

    const token = getAuthToken();
    try {
      const res = await fetch(`/companies/${company.id}/statistics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load statistics');
      const stats = await res.json();
      setCompanyStats(stats);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const activeCompanies = companies.filter((c) => c.is_active).length;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100' }}>
      {/* Navigation */}
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Super Admin Dashboard
          </Typography>
          <Button color="inherit" startIcon={<LogoutIcon />} onClick={handleLogout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Box className="admin-container">
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight={700}>
            Super Admin
          </Typography>
          <Typography color="text.secondary">
            Manage all companies and system settings
          </Typography>
        </Box>

        {/* User Info Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Your Account
            </Typography>
            <Typography>
              <strong>Username:</strong> {currentUser?.username}
            </Typography>
            <Typography>
              <strong>Email:</strong> {currentUser?.email}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <strong>Role:</strong>{' '}
              <Chip label="SUPER ADMIN" size="small" color="secondary" />
            </Box>
          </CardContent>
        </Card>

        {/* Stats Cards - Gentle styling with circular pastel icons */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mb: 3 }}>
          <Card sx={{ flex: '1 1 220px', minWidth: 220, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: '#dbeafe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                  flexShrink: 0,
                }}
              >
                <BusinessIcon sx={{ fontSize: 26, color: '#3b82f6' }} />
              </Box>
              <Box>
                <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                  Total Companies
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>
                  {companies.length}
                </Typography>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 220px', minWidth: 220, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: '#dcfce7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                  flexShrink: 0,
                }}
              >
                <GroupIcon sx={{ fontSize: 26, color: '#22c55e' }} />
              </Box>
              <Box>
                <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                  Active Companies
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>
                  {activeCompanies}
                </Typography>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 220px', minWidth: 220, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: '#f3e8ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2,
                  flexShrink: 0,
                }}
              >
                <PeopleIcon sx={{ fontSize: 26, color: '#a855f7' }} />
              </Box>
              <Box>
                <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                  Total Users
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>
                  {totalUsers}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Companies List */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">All Companies</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateModalOpen(true)}
              >
                Create Company
              </Button>
            </Box>

            {companies.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={4}>
                No companies yet. Create your first company!
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {companies.map((company) => (
                  <Card key={company.id} variant="outlined">
                    <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                          <Typography variant="h6">{company.name}</Typography>
                          <Chip
                            label={company.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            color={company.is_active ? 'success' : 'default'}
                          />
                          <Chip label={company.subscription_tier} size="small" color="primary" variant="outlined" />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Slug: {company.slug}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Email: {company.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Created: {new Date(company.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Access Company">
                          <IconButton color="secondary" onClick={() => handleAccessCompany(company)}>
                            <VpnKeyIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Invite User">
                          <IconButton
                            color="success"
                            onClick={() => {
                              setSelectedCompany(company);
                              setInviteModalOpen(true);
                            }}
                          >
                            <PersonAddIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Users">
                          <IconButton color="primary" onClick={() => handleViewUsers(company)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Statistics">
                          <IconButton color="info" onClick={() => handleViewStats(company)}>
                            <BarChartIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            onClick={() => {
                              setSelectedCompany(company);
                              setEditModalOpen(true);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton color="error" onClick={() => handleDeleteCompany(company)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Create Company Modal */}
      <CreateCompanyModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => {
          setCreateModalOpen(false);
          loadCompanies();
        }}
        providers={availableLLMProviders}
      />

      {/* Edit Company Modal */}
      <EditCompanyModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSuccess={() => {
          setEditModalOpen(false);
          loadCompanies();
        }}
        company={selectedCompany}
        providers={availableLLMProviders}
      />

      {/* Invite User Modal */}
      <InviteUserModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        company={selectedCompany}
      />

      {/* View Users Modal */}
      <ViewUsersModal
        open={usersModalOpen}
        onClose={() => setUsersModalOpen(false)}
        company={selectedCompany}
        users={companyUsers}
      />

      {/* View Statistics Modal */}
      <ViewStatisticsModal
        open={statsModalOpen}
        onClose={() => setStatsModalOpen(false)}
        company={selectedCompany}
        stats={companyStats}
      />
    </Box>
  );
}

// Create Company Modal Component
function CreateCompanyModal({ open, onClose, onSuccess, providers }) {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    email: '',
    subscription_tier: 'free',
    max_users: 10,
    max_custom_gpts: 5,
    max_storage_mb: 1000,
    max_tokens_per_month: '',
  });
  const [additionalProviders, setAdditionalProviders] = useState({});
  const [providerToAdd, setProviderToAdd] = useState('');
  const [embeddingProvider, setEmbeddingProvider] = useState({
    provider: 'openai',
    api_key: '',
    model: 'text-embedding-3-small',
    enabled: true,
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const providersContainerRef = useRef(null);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'name' ? { slug: value.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-') } : {}),
    }));
  };

  // Helper to extract model IDs from supported_models (handles both object and string formats)
  const extractModelIds = (supportedModels) => {
    if (!supportedModels) return [];
    return supportedModels.map((m) => (typeof m === 'object' ? m.id : m));
  };

  // Helper to get full model info from config (for checkbox display)
  const getProviderModels = (providerId) => {
    const providerConfig = providers.find((p) => p.id === providerId);
    if (!providerConfig?.supported_models) return [];
    return providerConfig.supported_models.map((m) => {
      if (typeof m === 'object') {
        return { id: m.id, supports_tools: m.supports_tools !== false };
      }
      return { id: m, supports_tools: true }; // backward compat: strings support tools
    });
  };

  // Toggle model selection
  const handleModelToggle = (providerKey, modelId, isChecked) => {
    const currentModels = additionalProviders[providerKey]?.available_models || [];
    let newModels;
    if (isChecked) {
      newModels = [...currentModels, modelId];
    } else {
      newModels = currentModels.filter((m) => m !== modelId);
    }
    handleProviderChange(providerKey, 'available_models', newModels);
  };

  const handleAddProvider = () => {
    if (!providerToAdd) return;

    const providerConfig = providers.find((p) => p.id === providerToAdd);
    const modelIds = extractModelIds(providerConfig?.supported_models);
    let providerKey = providerToAdd;
    let counter = 1;
    while (additionalProviders[providerKey]) {
      counter++;
      providerKey = `${providerToAdd}_${counter}`;
    }

    setAdditionalProviders((prev) => ({
      ...prev,
      [providerKey]: {
        name: providerConfig?.name || providerToAdd,
        provider: providerToAdd,
        enabled: true,
        api_key: '',
        default_model: modelIds[0] || '',
        available_models: modelIds,
        embedding_model: providerConfig?.embedding_models?.[0] || '',
        is_default: Object.keys(prev).length === 0,
      },
    }));
    setProviderToAdd('');

    // Scroll to the newly added provider after render
    setTimeout(() => {
      if (providersContainerRef.current) {
        const lastChild = providersContainerRef.current.lastElementChild;
        if (lastChild) {
          lastChild.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, 100);
  };

  const handleRemoveProvider = (key) => {
    setAdditionalProviders((prev) => {
      const newProviders = { ...prev };
      delete newProviders[key];
      return newProviders;
    });
  };

  const handleProviderChange = (key, field, value) => {
    setAdditionalProviders((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const handleSetDefault = (key) => {
    setAdditionalProviders((prev) => {
      const newProviders = {};
      Object.keys(prev).forEach((k) => {
        newProviders[k] = { ...prev[k], is_default: k === key };
      });
      return newProviders;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    const token = localStorage.getItem('cme_token');

    const companyData = {
      name: formData.name,
      slug: formData.slug,
      email: formData.email,
      subscription_tier: formData.subscription_tier,
      settings: {
        max_users: parseInt(formData.max_users) || 10,
        max_custom_gpts: parseInt(formData.max_custom_gpts) || 5,
        max_storage_mb: parseInt(formData.max_storage_mb) || 1000,
        max_tokens_per_month: formData.max_tokens_per_month ? parseInt(formData.max_tokens_per_month) : null,
        providers: Object.keys(additionalProviders).length > 0 ? additionalProviders : null,
        embedding_provider: embeddingProvider.api_key ? embeddingProvider : null,
      },
    };

    try {
      const res = await fetch('/companies', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(companyData),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create company');
      }

      alert('Company created successfully!');
      setFormData({
        name: '',
        slug: '',
        email: '',
        subscription_tier: 'free',
        max_users: 10,
        max_custom_gpts: 5,
        max_storage_mb: 1000,
        max_tokens_per_month: '',
      });
      setAdditionalProviders({});
      setEmbeddingProvider({
        provider: 'openai',
        api_key: '',
        model: 'text-embedding-3-small',
        enabled: true,
      });
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Create New Company
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ maxHeight: '70vh' }}>
          {/* Company Basic Info Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Company Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={formData.name}
                  onChange={handleChange('name')}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Slug"
                  value={formData.slug}
                  onChange={handleChange('slug')}
                  required
                  helperText="URL-friendly identifier (auto-generated from name)"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Subscription Tier</InputLabel>
                  <Select
                    value={formData.subscription_tier}
                    label="Subscription Tier"
                    onChange={handleChange('subscription_tier')}
                  >
                    <MenuItem value="free">Free</MenuItem>
                    <MenuItem value="starter">Starter</MenuItem>
                    <MenuItem value="professional">Professional</MenuItem>
                    <MenuItem value="enterprise">Enterprise</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* LLM Providers Section */}
          <Box sx={{ pt: 3, borderTop: '1px solid #e5e7eb' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                LLM Providers
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={providerToAdd}
                    onChange={(e) => setProviderToAdd(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">Select provider...</MenuItem>
                    {providers.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="contained" size="small" onClick={handleAddProvider} startIcon={<AddIcon />}>
                  Add
                </Button>
              </Box>
            </Box>

            {Object.keys(additionalProviders).length === 0 ? (
              <Typography color="text.secondary" fontStyle="italic">
                No providers configured. Select a provider above and click "Add" to configure one.
              </Typography>
            ) : (
              <Box ref={providersContainerRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {Object.entries(additionalProviders).map(([key, config]) => (
                  <Box
                    key={key}
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: config.is_default ? '#eff6ff' : '#f9fafb',
                      border: '1px solid',
                      borderColor: config.is_default ? '#93c5fd' : '#d1d5db',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {config.name || key}
                          {' '}
                          <Typography component="span" variant="subtitle1" color="text.secondary" fontWeight={400}>
                            ({providers.find(p => p.id === config.provider)?.name || config.provider || key.split('_')[0]})
                          </Typography>
                        </Typography>
                        {config.is_default && (
                          <Chip
                            label="Default"
                            size="small"
                            sx={{ bgcolor: '#2563eb', color: 'white', fontSize: '0.75rem', height: 22 }}
                          />
                        )}
                      </Box>
                      <IconButton size="small" color="error" onClick={() => handleRemoveProvider(key)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Checkboxes row */}
                      <Box sx={{ display: 'flex', gap: 3 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={config.enabled}
                              onChange={(e) => handleProviderChange(key, 'enabled', e.target.checked)}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">Enabled</Typography>}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={config.is_default}
                              onChange={() => handleSetDefault(key)}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">Set as Default</Typography>}
                        />
                      </Box>

                      {/* Name field */}
                      <Box>
                        <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>
                          Name
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          value={config.name || ''}
                          onChange={(e) => handleProviderChange(key, 'name', e.target.value)}
                          placeholder="e.g., Production, Development, Testing"
                          sx={{ bgcolor: 'white' }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Custom name shown to users when creating GPTs
                        </Typography>
                      </Box>

                      {/* API Key field */}
                      <Box>
                        <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>
                          API Key
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="password"
                          value={config.api_key || ''}
                          onChange={(e) => handleProviderChange(key, 'api_key', e.target.value)}
                          placeholder="API key"
                          sx={{ bgcolor: 'white' }}
                        />
                      </Box>

                      {/* Default Model field */}
                      <Box>
                        <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>
                          Default Model
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          value={config.default_model || ''}
                          onChange={(e) => handleProviderChange(key, 'default_model', e.target.value)}
                          placeholder="e.g., gpt-4o-mini, gemini-pro, claude-sonnet-4-5"
                          sx={{ bgcolor: 'white' }}
                        />
                      </Box>

                      {/* Available Models field */}
                      <Box>
                        <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>
                          Available Models
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 200, overflowY: 'auto', p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          {getProviderModels(config.provider || key.split('_')[0]).map((model) => (
                            <FormControlLabel
                              key={model.id}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={(config.available_models || []).includes(model.id)}
                                  onChange={(e) => handleModelToggle(key, model.id, e.target.checked)}
                                />
                              }
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{model.id}</Typography>
                                  {!model.supports_tools && (
                                    <Chip label="No Tools" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />
                                  )}
                                </Box>
                              }
                              sx={{ m: 0, '& .MuiFormControlLabel-label': { flex: 1 } }}
                            />
                          ))}
                          {getProviderModels(config.provider || key.split('_')[0]).length === 0 && (
                            <Typography variant="caption" color="text.secondary">No models configured for this provider</Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Embedding Provider Section */}
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e5e7eb' }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Embedding Provider (RAG)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure a dedicated embedding provider for RAG. This is separate from chat LLM providers.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={embeddingProvider.enabled}
                      onChange={(e) => setEmbeddingProvider(prev => ({ ...prev, enabled: e.target.checked }))}
                    />
                  }
                  label="Enable RAG / Embeddings"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Provider</InputLabel>
                  <Select
                    value={embeddingProvider.provider}
                    label="Provider"
                    onChange={(e) => {
                      const provider = e.target.value;
                      let defaultModel = 'text-embedding-3-small';
                      if (provider === 'gemini') defaultModel = 'gemini-embedding-001';
                      else if (provider === 'deepseek') defaultModel = 'deepseek-embedding-v3';
                      setEmbeddingProvider(prev => ({ ...prev, provider, model: defaultModel }));
                    }}
                  >
                    <MenuItem value="openai">OpenAI</MenuItem>
                    <MenuItem value="gemini">Google Gemini</MenuItem>
                    <MenuItem value="deepseek">DeepSeek</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="API Key"
                  type="password"
                  value={embeddingProvider.api_key}
                  onChange={(e) => setEmbeddingProvider(prev => ({ ...prev, api_key: e.target.value }))}
                  placeholder="Embedding API key"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Embedding Model"
                  value={embeddingProvider.model}
                  onChange={(e) => setEmbeddingProvider(prev => ({ ...prev, model: e.target.value }))}
                  placeholder={
                    embeddingProvider.provider === 'openai' ? 'text-embedding-3-small' :
                    embeddingProvider.provider === 'gemini' ? 'gemini-embedding-001' :
                    'Embedding model'
                  }
                />
              </Grid>
            </Grid>
          </Box>

          {/* Company Limits Section */}
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e5e7eb' }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Company Limits
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Max Users"
                  type="number"
                  value={formData.max_users}
                  onChange={handleChange('max_users')}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Max Custom GPTs"
                  type="number"
                  value={formData.max_custom_gpts}
                  onChange={handleChange('max_custom_gpts')}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Max Storage (MB)"
                  type="number"
                  value={formData.max_storage_mb}
                  onChange={handleChange('max_storage_mb')}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Max Tokens/Month"
                  type="number"
                  value={formData.max_tokens_per_month}
                  onChange={handleChange('max_tokens_per_month')}
                  placeholder="Unlimited"
                />
              </Grid>
            </Grid>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Create Company'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// Edit Company Modal Component
function EditCompanyModal({ open, onClose, onSuccess, company, providers }) {
  const [formData, setFormData] = useState({});
  const [additionalProviders, setAdditionalProviders] = useState({});
  const [providerToAdd, setProviderToAdd] = useState('');
  const [embeddingProvider, setEmbeddingProvider] = useState({
    provider: 'openai',
    api_key: '',
    model: 'text-embedding-3-small',
    enabled: true,
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const providersContainerRef = useRef(null);

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        slug: company.slug || '',
        email: company.email || '',
        subscription_tier: company.subscription_tier || 'free',
        is_active: company.is_active ?? true,
        max_users: company.settings?.max_users || 10,
        max_custom_gpts: company.settings?.max_custom_gpts || 5,
        max_storage_mb: company.settings?.max_storage_mb || 1000,
        max_tokens_per_month: company.settings?.max_tokens_per_month || '',
      });
      setAdditionalProviders(company.settings?.providers || {});
      setEmbeddingProvider(company.settings?.embedding_provider || {
        provider: 'openai',
        api_key: '',
        model: 'text-embedding-3-small',
        enabled: true,
      });
    }
  }, [company]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Helper to extract model IDs from supported_models (handles both object and string formats)
  const extractModelIds = (supportedModels) => {
    if (!supportedModels) return [];
    return supportedModels.map((m) => (typeof m === 'object' ? m.id : m));
  };

  // Helper to get full model info from config (for checkbox display)
  const getProviderModels = (providerId) => {
    const providerConfig = providers.find((p) => p.id === providerId);
    if (!providerConfig?.supported_models) return [];
    return providerConfig.supported_models.map((m) => {
      if (typeof m === 'object') {
        return { id: m.id, supports_tools: m.supports_tools !== false };
      }
      return { id: m, supports_tools: true };
    });
  };

  // Toggle model selection
  const handleModelToggle = (providerKey, modelId, isChecked) => {
    const currentModels = additionalProviders[providerKey]?.available_models || [];
    let newModels;
    if (isChecked) {
      newModels = [...currentModels, modelId];
    } else {
      newModels = currentModels.filter((m) => m !== modelId);
    }
    handleProviderChange(providerKey, 'available_models', newModels);
  };

  const handleAddProvider = () => {
    if (!providerToAdd) return;

    const providerConfig = providers.find((p) => p.id === providerToAdd);
    const modelIds = extractModelIds(providerConfig?.supported_models);
    let providerKey = providerToAdd;
    let counter = 1;
    while (additionalProviders[providerKey]) {
      counter++;
      providerKey = `${providerToAdd}_${counter}`;
    }

    setAdditionalProviders((prev) => ({
      ...prev,
      [providerKey]: {
        name: providerConfig?.name || providerToAdd,
        provider: providerToAdd,
        enabled: true,
        api_key: '',
        default_model: modelIds[0] || '',
        available_models: modelIds,
        embedding_model: providerConfig?.embedding_models?.[0] || '',
        is_default: Object.keys(prev).length === 0,
      },
    }));
    setProviderToAdd('');

    // Scroll to the newly added provider after render
    setTimeout(() => {
      if (providersContainerRef.current) {
        const lastChild = providersContainerRef.current.lastElementChild;
        if (lastChild) {
          lastChild.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, 100);
  };

  const handleRemoveProvider = (key) => {
    setAdditionalProviders((prev) => {
      const newProviders = { ...prev };
      delete newProviders[key];
      return newProviders;
    });
  };

  const handleProviderChange = (key, field, value) => {
    setAdditionalProviders((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  };

  const handleSetDefault = (key) => {
    setAdditionalProviders((prev) => {
      const newProviders = {};
      Object.keys(prev).forEach((k) => {
        newProviders[k] = { ...prev[k], is_default: k === key };
      });
      return newProviders;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    const token = localStorage.getItem('cme_token');
    const updates = {
      name: formData.name,
      slug: formData.slug,
      email: formData.email,
      subscription_tier: formData.subscription_tier,
      is_active: formData.is_active,
      settings: {
        max_users: parseInt(formData.max_users) || 10,
        max_custom_gpts: parseInt(formData.max_custom_gpts) || 5,
        max_storage_mb: parseInt(formData.max_storage_mb) || 1000,
        max_tokens_per_month: formData.max_tokens_per_month ? parseInt(formData.max_tokens_per_month) : null,
        providers: Object.keys(additionalProviders).length > 0 ? additionalProviders : null,
        embedding_provider: embeddingProvider.api_key ? embeddingProvider : null,
      },
    };

    try {
      const res = await fetch(`/companies/${company.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (!res.ok) throw new Error('Failed to update company');

      alert('Company updated successfully!');
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!company) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Edit Company
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ maxHeight: '70vh' }}>
          {/* Company Basic Info Section */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Company Information
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_active}
                    onChange={handleChange('is_active')}
                  />
                }
                label="Company is active"
              />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={formData.name}
                  onChange={handleChange('name')}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Slug"
                  value={formData.slug}
                  onChange={handleChange('slug')}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Subscription Tier</InputLabel>
                  <Select
                    value={formData.subscription_tier}
                    label="Subscription Tier"
                    onChange={handleChange('subscription_tier')}
                  >
                    <MenuItem value="free">Free</MenuItem>
                    <MenuItem value="starter">Starter</MenuItem>
                    <MenuItem value="professional">Professional</MenuItem>
                    <MenuItem value="enterprise">Enterprise</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* LLM Providers Section */}
          <Box sx={{ pt: 3, borderTop: '1px solid #e5e7eb' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                LLM Providers
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={providerToAdd}
                    onChange={(e) => setProviderToAdd(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">Select provider...</MenuItem>
                    {providers.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="contained" size="small" onClick={handleAddProvider} startIcon={<AddIcon />}>
                  Add
                </Button>
              </Box>
            </Box>

            {Object.keys(additionalProviders).length === 0 ? (
              <Typography color="text.secondary" fontStyle="italic">
                No providers configured. Select a provider above and click "Add" to configure one.
              </Typography>
            ) : (
              <Box ref={providersContainerRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {Object.entries(additionalProviders).map(([key, config]) => (
                  <Box
                    key={key}
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: config.is_default ? '#eff6ff' : '#f9fafb',
                      border: '1px solid',
                      borderColor: config.is_default ? '#93c5fd' : '#d1d5db',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {config.name || key}
                          {' '}
                          <Typography component="span" variant="subtitle1" color="text.secondary" fontWeight={400}>
                            ({providers.find(p => p.id === config.provider)?.name || config.provider || key.split('_')[0]})
                          </Typography>
                        </Typography>
                        {config.is_default && (
                          <Chip
                            label="Default"
                            size="small"
                            sx={{ bgcolor: '#2563eb', color: 'white', fontSize: '0.75rem', height: 22 }}
                          />
                        )}
                      </Box>
                      <IconButton size="small" color="error" onClick={() => handleRemoveProvider(key)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Checkboxes row */}
                      <Box sx={{ display: 'flex', gap: 3 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={config.enabled}
                              onChange={(e) => handleProviderChange(key, 'enabled', e.target.checked)}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">Enabled</Typography>}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={config.is_default}
                              onChange={() => handleSetDefault(key)}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">Set as Default</Typography>}
                        />
                      </Box>

                      {/* Name field */}
                      <Box>
                        <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>
                          Name
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          value={config.name || ''}
                          onChange={(e) => handleProviderChange(key, 'name', e.target.value)}
                          placeholder="e.g., Production, Development, Testing"
                          sx={{ bgcolor: 'white' }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Custom name shown to users when creating GPTs
                        </Typography>
                      </Box>

                      {/* API Key field */}
                      <Box>
                        <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>
                          API Key
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="password"
                          value={config.api_key || ''}
                          onChange={(e) => handleProviderChange(key, 'api_key', e.target.value)}
                          placeholder="API key"
                          sx={{ bgcolor: 'white' }}
                        />
                      </Box>

                      {/* Default Model field */}
                      <Box>
                        <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>
                          Default Model
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          value={config.default_model || ''}
                          onChange={(e) => handleProviderChange(key, 'default_model', e.target.value)}
                          placeholder="e.g., gpt-4o-mini, gemini-pro, claude-sonnet-4-5"
                          sx={{ bgcolor: 'white' }}
                        />
                      </Box>

                      {/* Available Models field */}
                      <Box>
                        <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>
                          Available Models
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxHeight: 200, overflowY: 'auto', p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          {getProviderModels(config.provider || key.split('_')[0]).map((model) => (
                            <FormControlLabel
                              key={model.id}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={(config.available_models || []).includes(model.id)}
                                  onChange={(e) => handleModelToggle(key, model.id, e.target.checked)}
                                />
                              }
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{model.id}</Typography>
                                  {!model.supports_tools && (
                                    <Chip label="No Tools" size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />
                                  )}
                                </Box>
                              }
                              sx={{ m: 0, '& .MuiFormControlLabel-label': { flex: 1 } }}
                            />
                          ))}
                          {getProviderModels(config.provider || key.split('_')[0]).length === 0 && (
                            <Typography variant="caption" color="text.secondary">No models configured for this provider</Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Embedding Provider Section */}
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e5e7eb' }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Embedding Provider (RAG)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure a dedicated embedding provider for RAG. This is separate from chat LLM providers.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={embeddingProvider.enabled}
                      onChange={(e) => setEmbeddingProvider(prev => ({ ...prev, enabled: e.target.checked }))}
                    />
                  }
                  label="Enable RAG / Embeddings"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Provider</InputLabel>
                  <Select
                    value={embeddingProvider.provider}
                    label="Provider"
                    onChange={(e) => {
                      const provider = e.target.value;
                      let defaultModel = 'text-embedding-3-small';
                      if (provider === 'gemini') defaultModel = 'gemini-embedding-001';
                      else if (provider === 'deepseek') defaultModel = 'deepseek-embedding-v3';
                      setEmbeddingProvider(prev => ({ ...prev, provider, model: defaultModel }));
                    }}
                  >
                    <MenuItem value="openai">OpenAI</MenuItem>
                    <MenuItem value="gemini">Google Gemini</MenuItem>
                    <MenuItem value="deepseek">DeepSeek</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="API Key"
                  type="password"
                  value={embeddingProvider.api_key}
                  onChange={(e) => setEmbeddingProvider(prev => ({ ...prev, api_key: e.target.value }))}
                  placeholder="Embedding API key"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Embedding Model"
                  value={embeddingProvider.model}
                  onChange={(e) => setEmbeddingProvider(prev => ({ ...prev, model: e.target.value }))}
                  placeholder={
                    embeddingProvider.provider === 'openai' ? 'text-embedding-3-small' :
                    embeddingProvider.provider === 'gemini' ? 'gemini-embedding-001' :
                    'Embedding model'
                  }
                />
              </Grid>
            </Grid>
          </Box>

          {/* Company Limits Section */}
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e5e7eb' }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Company Limits
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Max Users"
                  type="number"
                  value={formData.max_users}
                  onChange={handleChange('max_users')}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Max Custom GPTs"
                  type="number"
                  value={formData.max_custom_gpts}
                  onChange={handleChange('max_custom_gpts')}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Max Storage (MB)"
                  type="number"
                  value={formData.max_storage_mb}
                  onChange={handleChange('max_storage_mb')}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Max Tokens/Month"
                  type="number"
                  value={formData.max_tokens_per_month}
                  onChange={handleChange('max_tokens_per_month')}
                  placeholder="Unlimited"
                />
              </Grid>
            </Grid>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// Invite User Modal Component
function InviteUserModal({ open, onClose, company }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('user');
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);

    const token = localStorage.getItem('cme_token');
    try {
      const res = await fetch(`/companies/${company.id}/invitations`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, role }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to send invitation');
      }

      setResult({
        success: true,
        emailSent: data.email_sent,
        message: data.message,
        inviteUrl: data.invitation?.accept_url,
      });
      setEmail('');
    } catch (err) {
      setResult({ success: false, message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setRole('user');
    setResult(null);
    onClose();
  };

  if (!company) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Invite User
        <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            fullWidth
            label="Company"
            value={company.name}
            disabled
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select value={role} label="Role" onChange={(e) => setRole(e.target.value)}>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="company_admin">Company Admin</MenuItem>
              <MenuItem value="company_owner">Company Owner</MenuItem>
            </Select>
          </FormControl>

          {result && (
            <Alert severity={result.success ? 'success' : 'error'} sx={{ mt: 2 }}>
              {result.success ? (
                result.emailSent ? (
                  `Invitation sent to ${email}`
                ) : (
                  <>
                    <Typography variant="body2" gutterBottom>
                      {result.message || 'Invitation created. Email service not configured.'}
                    </Typography>
                    {result.inviteUrl && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1, wordBreak: 'break-all' }}>
                        <Typography variant="caption" fontFamily="monospace">
                          {window.location.origin}{result.inviteUrl}
                        </Typography>
                      </Box>
                    )}
                    <Button
                      size="small"
                      onClick={() => navigator.clipboard.writeText(`${window.location.origin}${result.inviteUrl}`)}
                      sx={{ mt: 1 }}
                    >
                      Copy Link
                    </Button>
                  </>
                )
              ) : (
                result.message
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Send Invitation'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// View Users Modal Component
function ViewUsersModal({ open, onClose, company, users }) {
  if (!company) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Company Users
        <Typography variant="body2" color="text.secondary">
          {company.name}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {users.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={4}>
            No users in this company yet.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {users.map((user) => (
              <Card key={user.id} variant="outlined">
                <CardContent sx={{ py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight={600}>{user.display_name || user.email}</Typography>
                      <Chip
                        label={user.role.replace('_', ' ').toUpperCase()}
                        size="small"
                        color={user.role === 'company_admin' ? 'primary' : 'default'}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {user.last_login
                        ? `Last login: ${new Date(user.last_login).toLocaleString()}`
                        : 'Never logged in'}
                    </Typography>
                  </Box>
                  <Chip
                    label={user.is_active ? 'Active' : 'Inactive'}
                    size="small"
                    color={user.is_active ? 'success' : 'error'}
                  />
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// View Statistics Modal Component
function ViewStatisticsModal({ open, onClose, company, stats }) {
  if (!company) return null;

  // Helper component for stat cards with icons
  const StatCard = ({ icon, iconBg, iconColor, label, value, subtext }) => (
    <Card sx={{ flex: '1 1 200px', minWidth: 200, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem' }}>{label}</Typography>
          <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.1 }}>{value}</Typography>
          {subtext && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{subtext}</Typography>}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Company Statistics
        <Typography variant="body2" color="text.secondary">
          {company.name}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {!stats ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Stats Cards - Row 1 */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mb: 2.5 }}>
              <StatCard
                icon={<PeopleIcon sx={{ fontSize: 26, color: '#3b82f6' }} />}
                iconBg="#dbeafe"
                label="Total Users"
                value={stats.users.total}
                subtext={`of ${stats.users.limit} limit`}
              />
              <StatCard
                icon={<CheckCircleIcon sx={{ fontSize: 26, color: '#22c55e' }} />}
                iconBg="#dcfce7"
                label="Active Users (7d)"
                value={stats.users.active_last_7_days}
              />
              <StatCard
                icon={<ChatIcon sx={{ fontSize: 26, color: '#a855f7' }} />}
                iconBg="#f3e8ff"
                label="Conversations"
                value={stats.threads.total.toLocaleString()}
              />
              <StatCard
                icon={<MessageIcon sx={{ fontSize: 26, color: '#0ea5e9' }} />}
                iconBg="#e0f2fe"
                label="Total Messages"
                value={stats.messages.total.toLocaleString()}
              />
            </Box>

            {/* Stats Cards - Row 2 */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mb: 3 }}>
              <StatCard
                icon={<BoltIcon sx={{ fontSize: 26, color: '#f59e0b' }} />}
                iconBg="#fef3c7"
                label="Tokens (Month)"
                value={stats.tokens.total_this_month.toLocaleString()}
                subtext={stats.tokens.limit_per_month
                  ? `${stats.tokens.percentage_used?.toFixed(1) || 0}% of ${stats.tokens.limit_per_month.toLocaleString()}`
                  : 'Unlimited'}
              />
              <StatCard
                icon={<SmartToyIcon sx={{ fontSize: 26, color: '#ef4444' }} />}
                iconBg="#fee2e2"
                label="Custom GPTs"
                value={stats.custom_gpts.total}
                subtext={`of ${stats.custom_gpts.limit} limit`}
              />
              <StatCard
                icon={<StorageIcon sx={{ fontSize: 26, color: '#6b7280' }} />}
                iconBg="#f3f4f6"
                label="Storage Used"
                value={`${stats.storage.used_mb} MB`}
                subtext={`of ${stats.storage.limit_mb} MB (${stats.storage.percentage_used}%)`}
              />
              <StatCard
                icon={<BarChartIcon sx={{ fontSize: 26, color: '#ec4899' }} />}
                iconBg="#fce7f3"
                label="Avg Msgs/Thread"
                value={stats.messages.average_per_thread}
              />
            </Box>

            {/* Top Users & GPTs */}
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 4 }}>
              <Card sx={{ flex: 1, minWidth: 300, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>Most Active Users</Typography>
                  {stats.top_users.by_messages.length === 0 ? (
                    <Typography color="text.secondary">No activity yet</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {stats.top_users.by_messages.map((user, index) => (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography fontWeight={700} color="text.secondary">{index + 1}</Typography>
                            <Box>
                              <Typography fontWeight={500}>{user.display_name}</Typography>
                              <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                            </Box>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography fontWeight={700}>{user.message_count}</Typography>
                            <Typography variant="caption" color="text.secondary">messages</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
              <Card sx={{ flex: 1, minWidth: 300, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>Most Used Custom GPTs</Typography>
                  {stats.custom_gpts.most_used.length === 0 ? (
                    <Typography color="text.secondary">No Custom GPTs used yet</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {stats.custom_gpts.most_used.map((gpt, index) => (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography fontWeight={700} color="text.secondary">{index + 1}</Typography>
                            <Typography fontWeight={500}>{gpt.name}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography fontWeight={700}>{gpt.thread_count}</Typography>
                            <Typography variant="caption" color="text.secondary">conversations</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
